import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../../utils/auth';
import { X, Calendar, Clock, Users, Truck } from 'lucide-react';
import '../../styles/admin/production/PlanVisitModal.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanVisitModal = ({ jobId, onClose, onVisitCreated }) => {
  const [teams, setTeams] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    scheduled_date: '',
    scheduled_time: '08:00',
    estimated_duration_minutes: 60,
    team_id: '',
    vehicle_id: '',
    notes_before: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, vehiclesRes] = await Promise.all([
        axios.get(`${API_URL}/api/teams`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/vehicles`, { headers: getAuthHeader() })
      ]);
      
      setTeams(teamsRes.data.filter(t => t.status === 'active'));
      setVehicles(vehiclesRes.data.filter(v => v.status === 'active'));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleTeamChange = (teamId) => {
    setFormData({ ...formData, team_id: teamId });
    
    // Préremplir le véhicule si l'équipe en a un assigné par défaut
    const selectedTeam = teams.find(t => t.id === teamId);
    if (selectedTeam && selectedTeam.assigned_vehicle_id) {
      setFormData(prev => ({ ...prev, team_id: teamId, vehicle_id: selectedTeam.assigned_vehicle_id }));
    } else {
      setFormData(prev => ({ ...prev, team_id: teamId }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.scheduled_date || !formData.team_id) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    
    try {
      const scheduled_start = `${formData.scheduled_date}T${formData.scheduled_time}:00.000Z`;
      
      const visitData = {
        job_id: jobId,
        scheduled_date: formData.scheduled_date,
        scheduled_start: scheduled_start,
        estimated_duration_minutes: parseInt(formData.estimated_duration_minutes),
        team_id: formData.team_id,
        vehicle_id: formData.vehicle_id || null,
        assigned_employee_ids: [],
        notes_before: formData.notes_before || null
      };

      await axios.post(
        `${API_URL}/api/job-visits`,
        visitData,
        { headers: getAuthHeader() }
      );

      onVisitCreated();
      onClose();
    } catch (error) {
      console.error('Error creating visit:', error);
      alert('Erreur lors de la création de la visite');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-plan-visit" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Planifier une visite</h3>
          <button onClick={onClose} className="btn-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>
                <Calendar size={16} />
                Date de la visite *
              </label>
              <input
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="form-group">
              <label>
                <Clock size={16} />
                Heure de début *
              </label>
              <input
                type="time"
                value={formData.scheduled_time}
                onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>
              Durée estimée (minutes) *
            </label>
            <input
              type="number"
              value={formData.estimated_duration_minutes}
              onChange={(e) => setFormData({ ...formData, estimated_duration_minutes: e.target.value })}
              min="15"
              step="15"
              required
            />
          </div>

          <div className="form-group">
            <label>
              <Users size={16} />
              Équipe assignée *
            </label>
            <select
              value={formData.team_id}
              onChange={(e) => handleTeamChange(e.target.value)}
              required
            >
              <option value="">Sélectionner une équipe</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name} ({team.code})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>
              <Truck size={16} />
              Véhicule
            </label>
            <select
              value={formData.vehicle_id}
              onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
            >
              <option value="">Aucun véhicule</option>
              {vehicles.map(vehicle => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.name} ({vehicle.plate || 'Sans plaque'})
                </option>
              ))}
            </select>
            <small className="form-hint">Le véhicule par défaut de l'équipe est prérempli si disponible</small>
          </div>

          <div className="form-group">
            <label>Notes de planification</label>
            <textarea
              value={formData.notes_before}
              onChange={(e) => setFormData({ ...formData, notes_before: e.target.value })}
              rows={3}
              placeholder="Instructions spéciales, préparation requise..."
            />
          </div>

          <div className="modal-actions">
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? 'Création...' : 'Planifier la visite'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanVisitModal;
